<template>
  <Modal
    :isModal="isShowDeleteModal"
    @select="confirmDelete"
    @close="close"
    :title="$t('confirmation')"
    :label="$t('DeleteConfirmationMSG')"
  />
  <div :class="{ '!w-full': attrs.full_width }">
    <button
      class="rounded-md bg-primary/80 hover:bg-primary flex items-center hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed"
      @click.prevent="addDraft"
    >
      <div class="flex justify-center items-center h-full bg-black/10 p-2 rounded-s">
        <i class="fa fa-plus text-white"></i>
      </div>
      <span class="px-1.5 ltr:font-sans tracking-wide text-white text-sm pb-[1px]">
        {{ $t('AddnewApplication') }}
      </span>
    </button>
    <DataTableSkeleton v-if="isLoading"></DataTableSkeleton>
    <DataTableVue
      v-else-if="dataSet.length > 0"
      :default-headers="[
        'RequestNumber',
        'OwnerName',
        'CreationDate',
        'DraftStatus',
        'isParentRequest',
        'operation'
      ]"
      :data-set="dataSet"
    >
      <template #headerCell="{ label }">
        <span v-if="label == 'operation'"></span>
      </template>
      <template #cell="{ columnName, columnValue, rowIndex }">
        <div v-if="columnName == 'OwnerName'" class="w-full h-full flex flex-col gap-1">
          <div>{{ columnValue[0] }}</div>
          <div>{{ columnValue[1] }}</div>
        </div>
        <div v-if="columnName == 'isParentRequest'" class="w-full h-full flex gap-1">
          <input
            type="checkbox"
            id="checkbox"
            @change="changeStatus($event, rowIndex)"
            v-model="dataSet[rowIndex].isParentRequest"
          />
        </div>
        <div v-if="columnName == 'operation'" class="w-full h-full flex gap-1">
          <div class="flex items-center justify-center gap-2 w-20 h-10">
            <button
              class="bg-primary size-6 rounded-md text-white cursor-pointer"
              @click.prevent="editDraft(rowIndex)"
            >
              <i class="fa fa-edit"></i>
            </button>
            <button
              class="text-primary border border-primary size-6 rounded-md cursor-pointer"
              v-if="canBeDelete(rowIndex)"
              @click.prevent="deleteDraft(rowIndex)"
            >
              <i class="fa fa-trash"></i>
            </button>
          </div>
        </div>
      </template>
    </DataTableVue>
    <div v-else class="w-full p-2 bg-brand-gray-250 text-center">
      {{ $t('noDataFound') }}
    </div>
  </div>
</template>

<script setup>
import DataTableVue from '@/components/DataTable/index.vue'
import DataTableSkeleton from '@/components/skeletons/DatatableSkeleton.vue'
import { useFormStore } from '@/stores/form'
import clientLookups from '@/assets/json/clientLookups.json'
import { onMounted, ref, useAttrs, nextTick } from 'vue'
import apiHelperService from '@/services'
import { useRoute, useRouter } from 'vue-router'
import { DateTime } from 'luxon'
import { notify } from 'notiwind'
import { useI18n } from 'vue-i18n'

import Modal from '@/components/Modal/index.vue'

const attrs = useAttrs()
const isLoading = ref(true)
const i18n = useI18n()

const dataSet = ref([])
const formStore = useFormStore()
const route = useRoute()
const router = useRouter()
const allList = ref()
const isShowDeleteModal = ref(false)
const currentDeleteIndex = ref()

async function selectShip(shipIndex) {
  const { registeredShips } = formStore.extraMap
  const { shipNumber, nationalityId: shipNationalityId, shipTypeId } = registeredShips[shipIndex]

  Object.assign(formStore.extraMap, {
    selectedShipIndex: shipIndex
  })

  formStore.form.setValues({
    shipNumber,
    shipNationalityId,
    shipTypeId
  })

  Object.assign(formStore.draft.request, {
    shipNumber,
    shipNationalityId,
    shipTypeId
  })
}
const canBeDelete = (rowIndex) => {
  const res =
    !dataSet.value[rowIndex].isParentRequest &&
    dataSet.value[rowIndex].RequestNumber !=
      (formStore.draft.requestNumber || route.query.requestNumber)
  return res
}
const close = () => {
  isShowDeleteModal.value = false
}
const confirmDelete = async () => {
  const currentDraftItem = allList.value[currentDeleteIndex.value]
  try {
    await apiHelperService.post('/api/draft/delete', [currentDraftItem.id])
    notify(
      {
        group: 'success',
        title: i18n.t('TitleSuccess'),
        text: i18n.t('DeletedSuccessfully')
      },
      6000
    )
  } catch (error) {
    console.error('delete draft error: ', error)
  }
  close()
  getTableData()
}

const deleteDraft = async (index) => {
  isShowDeleteModal.value = true
  currentDeleteIndex.value = index
}

const addDraft = async () => {
  const currentPrimaryDraft = allList.value.find((item) => item.isParentRequest)
  const { administrativeRegionId, withException } = formStore.draft
  await formStore.$reset()
  delete route.query.requestNumber
  nextTick(async () => {
    await formStore.resetForm(route.params.serviceId, true)
    router.push({
      path: route.path,
      query: {
        ...(administrativeRegionId && { administrativeRegionId }),
        ...(withException && { withException }),
        ...(currentPrimaryDraft.id && { refDraftId: currentPrimaryDraft.id })
      }
    })
  })
}

const editDraft = async (rowIndex) => {
  const currentPrimaryDraft = allList.value.find((item) => item.isParentRequest)
  const { administrativeRegionId, withException } = formStore.draft
  router.replace({
    path: route.path,
    query: {
      ...route.query,
      requestNumber: dataSet.value[rowIndex].RequestNumber,
      ...(administrativeRegionId && { administrativeRegionId }),
      ...(withException && { withException }),
      ...(currentPrimaryDraft.id && { refDraftId: currentPrimaryDraft.id })
    }
  })
  formStore.activeStep = formStore.schema.stepLabels.web[0]
}

const changeStatus = async (event, idx) => {
  const draft = allList.value[idx]

  await apiHelperService.post('/api/draft/setGroupParent', `"${draft.requestNumber}"`, {
    headers: {
      'content-type': 'application/json'
    }
  })
  await getTableData()
}

const getTableData = async () => {
  if (formStore.draft.request) {
    const {
      requestNumber,
      request: { applicantEmail }
    } = formStore.draft

    const searchCriteria = {
      pageSize: 10,
      currentPage: 1,
      getChildDraft: true,
      requestNumber: route.query.requestNumber || requestNumber,
      email: applicantEmail
    }
    const { ServiceTransactions } = clientLookups
    const response = await apiHelperService.post('/api/draft/search', searchCriteria, {
      params: {
        pageSize: 10,
        currentPage: 1
      }
    })
    allList.value = response?.collection
    dataSet.value = response?.collection
      ?.filter((e) => e.serviceTransactionId != ServiceTransactions.leavePermit)
      ?.map((draft) => {
        return {
          RequestNumber: draft.requestNumber,
          OwnerName: [draft.arabicReqBenficiaryName, draft.englishReqBenficiaryName],
          CreationDate: DateTime.fromJSDate(new Date(draft.creationDate)).toFormat(
            'dd/MM/yyyy hh:mm:ss a'
          ),
          DraftStatus: draft.draftStatus.text,
          isParentRequest: draft.isParentRequest,
          operation: ''
        }
      })
  }
}

onMounted(async () => {
  await getTableData()

  isLoading.value = false
})
</script>

<script>
export default {
  name: 'DataTableForm'
}
</script>
<style lang="scss" scoped></style>
